<template>
  <b-card
    no-body
    :class="cardBorderClass"
    style="border-width: 2px"
  >
    <!-- Header -->
    <b-card-header class="pb-50">
      <div class="d-flex justify-content-between align-items-center w-100">
        <div class="d-flex align-items-center">
          <span
            v-if="countryCode"
            class="flag-icon flag-icon-rounded mr-50"
            :class="`flag-icon-${countryCode}`"
          />
          <strong>{{ item.name }}</strong>
        </div>
        <b-badge :variant="statusVariant">
          {{ statusLabel }}
        </b-badge>
      </div>
      <small class="text-muted mt-25 d-block">
        {{ roundDateDisplay }}
      </small>
    </b-card-header>

    <hr class="my-0">

    <!-- Winning Numbers — single line, fixed height -->
    <b-card-body class="py-75">
      <div
        v-if="hasResult"
        class="d-flex align-items-baseline justify-content-center"
        style="min-height: 28px"
      >
        <template v-if="item.type === 1">
          <small class="text-muted mr-50">{{ $t('รางวัลที่1') }}</small>
          <span
            style="font-size: 1.2rem; font-weight: bold; letter-spacing: 2px"
            class="mr-1"
          >{{ d.topThree }}</span>
          <small class="text-muted mr-50">{{ $t('2ตัวล่าง') }}</small>
          <span style="font-weight: 600">{{ d.bottomTwo }}</span>
        </template>
        <template v-else-if="item.type === 5">
          <small class="text-muted mr-50">{{ $t('เลขชุด') }}</small>
          <span style="font-size: 1.2rem; font-weight: bold; letter-spacing: 2px">{{ d.fourNumber }}</span>
        </template>
        <template v-else>
          <small class="text-muted mr-50">{{ $t('3ตัวบน') }}</small>
          <span
            style="font-size: 1.2rem; font-weight: bold; letter-spacing: 2px"
            class="mr-1"
          >{{ topThreeDisplay }}</span>
          <template v-if="d.bottomTwo">
            <small class="text-muted mr-50">{{ $t('2ตัวล่าง') }}</small>
            <span style="font-weight: 600">{{ d.bottomTwo }}</span>
          </template>
        </template>
      </div>
      <div
        v-else
        class="text-muted text-center"
        style="min-height: 28px; line-height: 28px"
      >
        <small>{{ $t('ปิดรอบ:') }}</small> {{ item.roundEndDate || '-' }}
      </div>
    </b-card-body>

    <hr class="my-0">

    <!-- Financial Summary -->
    <b-card-body class="pt-1">
      <template v-if="d.summary">
        <div class="d-flex justify-content-between mb-25">
          <span class="text-muted">{{ $t('ยอดแทง') }}</span>
          <span>฿{{ formatNumber(d.summary.sumRollingAmount) }}</span>
        </div>
        <div class="d-flex justify-content-between mb-25">
          <span class="text-muted">{{ $t('ยอดจ่าย') }}</span>
          <span>฿{{ formatNumber(d.summary.sumWinAmount) }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <span class="text-muted">{{ $t('กำไร/ขาดทุน') }}</span>
          <span :class="profitColorClass">
            {{ profitPrefix }}฿{{ formatNumber(Math.abs(profit)) }}
          </span>
        </div>
      </template>
      <template v-else>
        <div class="d-flex justify-content-between mb-25">
          <span class="text-muted">{{ $t('ยอดแทง') }}</span>
          <span class="text-muted">-</span>
        </div>
        <div class="d-flex justify-content-between mb-25">
          <span class="text-muted">{{ $t('ยอดจ่าย') }}</span>
          <span class="text-muted">-</span>
        </div>
        <div class="d-flex justify-content-between">
          <span class="text-muted">{{ $t('กำไร/ขาดทุน') }}</span>
          <span class="text-muted">-</span>
        </div>
      </template>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BBadge,
} from 'bootstrap-vue'
import { NumberFormatter } from '@core/utils/filter'
import { getCountryCode } from '@core/utils/countryFlag'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BBadge,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    useLastResult: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      statusText: [
        'ยังไม่ถึงเวลาแทง',
        'เปิดรับแทง',
        'พักชั่วคราว',
        'ปิดรับแทง',
        'กำลังคำนวณ',
        'คำนวณเรียบร้อย',
        'รอจ่าย',
        'กำลังจ่าย',
        'คืนโพย',
        'จ่ายเสร็จ',
      ],
      statusColorMap: {
        0: 'secondary',
        1: 'success',
        2: 'danger',
        3: 'warning',
        4: 'secondary',
        5: 'dark',
        6: 'warning',
        7: 'info',
        8: 'secondary',
        9: 'light-secondary',
      },
    }
  },
  computed: {
    // เลือกข้อมูลตาม mode: lastResult หรือ current
    d() {
      if (this.useLastResult && this.item.lastResult) return this.item.lastResult
      return this.item
    },
    hasResult() {
      if (this.item.type === 5) return !!this.d.fourNumber
      return !!this.topThreeDisplay || !!this.d.bottomTwo
    },
    countryCode() {
      return getCountryCode(this.item.name)
    },
    statusLabel() {
      return this.$t(this.statusText[this.item.status] || 'ไม่ทราบสถานะ')
    },
    statusVariant() {
      return this.statusColorMap[this.item.status] || 'secondary'
    },
    roundDateDisplay() {
      if (!this.item.roundDate) return ''
      if (this.useLastResult && this.d.roundDate && this.d.roundDate !== this.item.roundDate) {
        return this.$t('ผลรอบ: {date}', { date: this.d.roundDate.split(' ')[0] })
      }
      return this.$t('รอบ: {date}', { date: this.item.roundDate.split(' ')[0] })
    },
    topThreeDisplay() {
      if (!this.d.topThree) return ''
      if (this.d.topThree.length >= 3) {
        return this.d.topThree.substring(this.d.topThree.length - 3)
      }
      return this.d.topThree
    },
    profit() {
      if (!this.d.summary) return 0
      return this.d.summary.sumRollingAmount - this.d.summary.sumWinAmount
    },
    profitPrefix() {
      if (this.profit > 0) return '+'
      if (this.profit < 0) return '-'
      return ''
    },
    profitColorClass() {
      if (this.profit > 0) return 'text-success font-weight-bold'
      if (this.profit < 0) return 'text-danger font-weight-bold'
      return ''
    },
    cardBorderClass() {
      if (!this.d.summary) return ''
      if (this.profit > 0) return 'border-success'
      if (this.profit < 0) return 'border-danger'
      return ''
    },
  },
  methods: {
    formatNumber(val) {
      if (val === null || val === undefined) return '0.00'
      return NumberFormatter(Number(val).toFixed(2))
    },
  },
}
</script>
